import { useEffect, useState } from 'react'

import { useCustomerId } from 'features/customers/hooks/useCustomerId'
import {
  type FeatureEdits,
  hasEdits as hasAnyEdits,
  isUnchanged,
  prepareFeatureEdits,
} from 'features/customers/utils/featureEdits'
import type { FeatureRow } from 'features/customers/utils/featureRows'
import omit from 'lodash/omit'

export const useFeatureEdits = (rows: FeatureRow[]) => {
  const customerId = useCustomerId()

  const [edits, setEdits] = useState<FeatureEdits>({})
  const [note, setNote] = useState('')
  const [resetOpen, setResetOpen] = useState(false)

  // routeWithLayout keys on the route pattern, so the tab does not remount between customers -
  // without this, edits typed for one account would be saved to the next, and an open reset dialog
  // would retarget the new one and clear whoever you landed on.
  // biome-ignore lint/correctness/useExhaustiveDependencies: customerId is the trigger, not an input
  useEffect(() => {
    setEdits({})
    setNote('')
    setResetOpen(false)
  }, [customerId])

  const discard = () => {
    setEdits({})
    setNote('')
  }

  // Typing a value back to what is stored is not an edit, so the row stops counting as one.
  const change = (name: string, value: unknown) =>
    setEdits(current => {
      const row = rows.find(candidate => candidate.name === name)

      if (row && isUnchanged(row, value)) return omit(current, name)

      return { ...current, [name]: value }
    })

  return {
    ...prepareFeatureEdits(edits, rows),
    edits,
    hasEdits: hasAnyEdits(edits),
    change,
    discard,
    note,
    setNote,
    resetOpen,
    openReset: () => setResetOpen(true),
    closeReset: () => setResetOpen(false),
  }
}
